import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import RedeemService from '../../../services/redeem.service';
import assistantService from '../../../services/assistant.service';

const btnPrimary = {
  width: '100%', background: '#7c3aed', color: 'white', border: 'none',
  borderRadius: 10, padding: '13px 0', fontSize: 14, fontWeight: 600, cursor: 'pointer',
  textAlign: 'center',
};

const btnSecondary = {
  width: '100%', background: 'transparent', border: '1px solid #2a2a40',
  color: '#888', borderRadius: 10, padding: '11px 0', fontSize: 13, cursor: 'pointer',
  marginTop: 10, textAlign: 'center',
};

const btnGhost = {
  fontSize: 10, color: '#a78bfa', background: 'rgba(124,58,237,0.1)',
  border: '1px solid rgba(124,58,237,0.2)', borderRadius: 6, padding: '5px 12px', cursor: 'pointer',
};

export default function Step4Preview({ wizard }) {
  const { templateData, redemptionData } = wizard;
  // El JWT puede venir del wizard (login clásico) o de localStorage: al
  // verificar el correo el backend emite el JWT y lo guardamos ahí
  // (access_token), no en el wizard. Reconocer ambas fuentes para no mandar
  // al usuario a /Login cuando ya está logueado por el enlace.
  const jwtToken = wizard.jwtToken || localStorage.getItem('access_token');
  const navigate = useNavigate();
  const [activating, setActivating] = useState(false);
  const [activateError, setActivateError] = useState('');
  const [nameEditing, setNameEditing] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [savingName, setSavingName] = useState(false);

  // templateData comes from previewCoupon: { template_name, includes: { assistant, workflows: [] } }
  // Replace {company_name} placeholder with the name the user entered in Step 2.
  // Si el estado del wizard llegó vacío (p.ej. vía el enlace de verificación en
  // otra pestaña), caer a la key propia del redeem; y por último "tu empresa".
  const company =
    (wizard.companyName || '').trim() ||
    (localStorage.getItem('redeem_company_name') || '').trim() ||
    'tu empresa';
  const fill = (s) => (s || '').replace(/\{company_name\}/g, company);
  const assistantName = fill(templateData?.includes?.assistant || templateData?.template_name || 'Agente');
  const displayName = editedName || assistantName;

  async function saveName() {
    const newName = editedName.trim();
    const assistantId = redemptionData?.assistant_id;
    if (!newName || !assistantId) { setNameEditing(false); return; }
    setSavingName(true);
    try {
      await assistantService.updateAssistant(assistantId, { name: newName });
      setNameEditing(false);
    } finally {
      setSavingName(false);
    }
  }
  const workflows = (templateData?.includes?.workflows || []).map(fill);
  const avatarUrl = templateData?.avatar_url || '';
  const avatarName = fill(templateData?.avatar_name || '');

  async function handleActivate() {
    if (!jwtToken) {
      navigate('/Login');
      return;
    }
    setActivating(true);
    setActivateError('');
    try {
      // Fallback a la key propia del redeem (OrganizationContext borra currentAccountId,
      // y el redemptionData se pierde si se vino del enlace del correo).
      const accountId = redemptionData?.account_id || localStorage.getItem('redeem_account_id');
      let workflowId = redemptionData?.workflow_ids?.[0];
      if (!workflowId) {
        try {
          const ids = JSON.parse(localStorage.getItem('redeem_workflow_ids') || 'null');
          workflowId = Array.isArray(ids) ? ids[0] : undefined;
        } catch (_) { /* ignore */ }
      }
      if (accountId && workflowId && wizard.selectedChannel) {
        await RedeemService.updateChannel({
          account_id: accountId,
          workflow_id: workflowId,
          channel: wizard.selectedChannel,
          integration_id: wizard.integrationIdRef?.current ?? wizard.integrationId,
        });
      }
      // Set the new account as current so the dashboard loads with the right
      // account instead of restoring a stale one from a previous session.
      // Clear the cached personal-account shortcut so the dashboard doesn't
      // try to fetch a stale ID from a previous session and get a 403.
      localStorage.removeItem('default_account_id');
      if (accountId) {
        // El contexto de ORGANIZATION se identifica por organization_id, NO por
        // account_id (usar account_id llevaba a la cuenta personal). El meta lleva
        // el organization_id para que OrganizationContext reconstruya la org.
        const organizationId = redemptionData?.organization_id || localStorage.getItem('redeem_organization_id');
        localStorage.setItem('currentAccountId', accountId);
        localStorage.setItem('currentAccountType', 'ORGANIZATION');
        localStorage.setItem('currentAccountMeta', JSON.stringify({
          id: accountId,
          type: 'ORGANIZATION',
          organization_id: organizationId || null,
          name: company !== 'tu empresa' ? company : undefined,
        }));
        localStorage.setItem('currentContext', JSON.stringify({
          type: 'ORGANIZATION',
          id: organizationId || accountId,
          account_id: accountId,
          organization_id: organizationId || null,
        }));
      } else {
        localStorage.removeItem('currentAccountId');
        localStorage.removeItem('currentAccountType');
        localStorage.removeItem('currentAccountMeta');
        localStorage.removeItem('currentContext');
      }

      const projectId = redemptionData?.project_id;
      const url = projectId
        ? `/dashboard?view=Projects&projectId=${projectId}&tab=workflows`
        : '/dashboard?view=Projects&tab=workflows';
      window.location.href = url;
    } catch (err) {
      setActivating(false);
      setActivateError('Error al activar. Intenta nuevamente.');
    }
  }

  return (
    <div>
      {/* Banner listo */}
      <div style={{
        background: 'rgba(124,58,237,0.08)', border: '1px solid rgba(124,58,237,0.25)',
        borderRadius: 12, padding: '14px 16px', textAlign: 'center', marginBottom: 20,
      }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#a78bfa', marginBottom: 4 }}>
          Listo para activar
        </div>
        <div style={{ fontSize: 12, color: '#888' }}>Revisa lo que se creara con tu template</div>
      </div>

      {/* Asistente */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 10, color: '#555', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
          Agente
        </div>
        <div style={{ background: '#0f0f1a', border: '1px solid #2a2a40', borderRadius: 10, padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 12 }}>
          {avatarUrl ? (
            <img src={avatarUrl} alt="" style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
          ) : (
            <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'rgba(124,58,237,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#a78bfa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2a4 4 0 0 1 4 4c0 1.5-.8 2.8-2 3.5V11h2a2 2 0 0 1 2 2v1h1a1 1 0 0 1 0 2h-1v1a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2v-1H5a1 1 0 0 1 0-2h1v-1a2 2 0 0 1 2-2h2V9.5C8.8 8.8 8 7.5 8 6a4 4 0 0 1 4-4z"/>
                <circle cx="9.5" cy="14.5" r="0.5" fill="#a78bfa"/>
                <circle cx="14.5" cy="14.5" r="0.5" fill="#a78bfa"/>
              </svg>
            </div>
          )}
          {nameEditing ? (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flex: 1 }}>
              <input
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                autoFocus
                style={{ flex: 1, background: '#0f0f1a', border: '1px solid #2a2a40', borderRadius: 8, padding: '6px 10px', color: '#e2e2f0', fontSize: 13 }}
              />
              <button onClick={saveName} disabled={savingName} style={btnGhost}>
                {savingName ? '...' : 'Guardar'}
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: '#e2e2f0' }}>{displayName}</div>
              <button
                onClick={() => { setEditedName(displayName); setNameEditing(true); }}
                style={{ ...btnGhost, marginLeft: 'auto' }}
              >
                Renombrar
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Avatar (AssistantTemplate) */}
      {avatarName && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 10, color: '#555', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
            Avatar
          </div>
          <div style={{ background: '#0f0f1a', border: '1px solid #2a2a40', borderRadius: 10, padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 30, height: 30, borderRadius: 8, background: 'rgba(124,58,237,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#a78bfa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                <circle cx="12" cy="7" r="4"/>
              </svg>
            </div>
            <div style={{ fontSize: 12, fontWeight: 500, color: '#e2e2f0' }}>{avatarName}</div>
          </div>
        </div>
      )}

      {/* Workflows */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 10, color: '#555', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
          Workflows ({workflows.length})
        </div>
        <div style={{ background: '#0f0f1a', border: '1px solid #2a2a40', borderRadius: 10, padding: '12px 14px' }}>
          {workflows.length === 0 && (
            <div style={{ fontSize: 12, color: '#555' }}>Sin workflows definidos</div>
          )}
          {workflows.map((wf, i) => (
            <div
              key={i}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                paddingTop: i > 0 ? 10 : 0,
                marginTop: i > 0 ? 10 : 0,
                borderTop: i > 0 ? '1px solid #1e1e35' : 'none',
              }}
            >
              <div style={{ width: 30, height: 30, borderRadius: 8, background: 'rgba(124,58,237,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#a78bfa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="7" height="7" rx="1"/>
                  <rect x="14" y="3" width="7" height="7" rx="1"/>
                  <rect x="3" y="14" width="7" height="7" rx="1"/>
                  <path d="M17.5 17.5m-2.5 0a2.5 2.5 0 1 0 5 0a2.5 2.5 0 1 0 -5 0"/>
                </svg>
              </div>
              <div style={{ fontSize: 12, fontWeight: 500, color: '#e2e2f0' }}>{typeof wf === 'string' ? wf : wf.name}</div>
            </div>
          ))}
        </div>
      </div>

      {!jwtToken && (
        <div style={{ fontSize: 11, color: '#888', textAlign: 'center', marginBottom: 12 }}>
          Revisa tu correo para obtener tu contrasena temporal y acceder.
        </div>
      )}

      {activateError && (
        <div style={{ fontSize: 12, color: '#f87171', textAlign: 'center', marginBottom: 12 }}>
          {activateError}
        </div>
      )}

      <button
        onClick={handleActivate}
        disabled={activating}
        style={{ ...btnPrimary, opacity: activating ? 0.6 : 1, cursor: activating ? 'not-allowed' : 'pointer' }}
      >
        {activating ? 'Activando...' : 'Activar y entrar'}
      </button>
      <button onClick={() => wizard.goToStep(4)} style={btnSecondary}>Atras</button>
    </div>
  );
}
